/**
 * PositionMutationTracker — runtime instrument that logs every write to
 * the local player's `position.{x,y}` with caller-stack source labels.
 *
 * LRG.16 (2026-05-13). Built in response to DJ's observation that even
 * with the Reconcile Toggle on (gating snap/smoothCorrect/accumulatedSnap),
 * something else was still pulling client position back toward server.
 * Suspected hidden mutators: noPredicted, debug:forceSnapToSchema,
 * net:message:playerTeleported, net:message:positionCorrection.
 *
 * Architecture:
 *   - Singleton with `enabled` flag (default OFF — zero overhead in prod)
 *   - `instrument(positionObj)` swaps the {x,y} fields for getter/setter pairs
 *     that capture writes and record a Mutation entry
 *   - Stack frame is captured ON WRITE (via `new Error().stack`) but parsed
 *     LAZILY on getStats / getRecent (cheap-path: just store the raw string)
 *   - Ring buffer cap 200 entries; per-source counter map (cumulative + 10s)
 *   - Emits a structured `[pos-mutate]` gameLogger.warn for MCP grep visibility
 *
 * Source labeling:
 *   - "Source" = first stack frame outside the Tracker itself + standard
 *     noise (Object.set, Proxy traps, react-internal, raf). Includes
 *     filename:line and function name when inferable.
 *   - Common labels we expect to see:
 *     - `EntitySystem.ts:614 (applyCommand)`  ← legit local prediction
 *     - `EntitySystem.ts:298 (noPredicted)`   ← the suspected hidden mutator
 *     - `PhysicsCoordinator.ts:218 (integratePosition)` ← legit local physics
 *
 * Default: OFF. Enable by:
 *   - URL: `?postrack=1`
 *   - Runtime: `PositionMutationTracker.setEnabled(true)` (from the
 *     debug tool's checkbox)
 *   - Programmatic: `PositionMutationTracker.enabled = true` (devtools)
 */

import { browserOnly } from '../../shared/util/browserOnly';
import { gameLogger } from '../../utils/GameLogger';
import { bootSearchParams } from '../../config/boot/bootUrl';

export interface PositionMutation {
  /** ms epoch */
  at: number;
  /** Which axis was written. */
  axis: 'x' | 'y';
  /** Previous value before write. */
  prevValue: number;
  /** New value (the one being set). */
  newValue: number;
  /** Delta = new - prev. */
  delta: number;
  /** Raw stack string captured at write time. Parsed lazily on read. */
  rawStack: string;
}

export interface PositionMutationStats {
  enabled: boolean;
  instrumented: boolean;
  totalMutations: number;
  mutationsLast10s: number;
  perSourceTotal: Array<{ source: string; count: number }>;
  perSourceLast10s: Array<{ source: string; count: number }>;
  recentMutations: Array<{
    at: number;
    axis: 'x' | 'y';
    prevValue: number;
    newValue: number;
    delta: number;
    source: string;
  }>;
}

const STORAGE_FLAG_NAME = 'postrack';
const RING_CAP = 200;
const WINDOW_MS = 10_000;
const STRUCTURED_LOG_MIN_GAP_MS = 50;  // Per-source throttle for gameLogger emit

class PositionMutationTrackerImpl {
  enabled = false;
  /** True after `instrument()` has been called on a position object. */
  instrumented = false;
  private buffer: PositionMutation[] = [];
  private totalCount = 0;
  private lastEmitBySource: Map<string, number> = new Map();

  /** Initialize from URL flag at construction time. After that, only the
   *  runtime setter mutates it (so the tool checkbox can flip live). */
  constructor() {
    browserOnly(() => {
      try {
        const params = bootSearchParams();
        if (params.get(STORAGE_FLAG_NAME) === '1') {
          this.enabled = true;
        }
      } catch { /* ignore */ }
    });
  }

  setEnabled(value: boolean): void {
    this.enabled = value;
    if (!value) {
      // Clear the per-source throttle map so the next re-enable starts fresh.
      this.lastEmitBySource.clear();
    }
  }

  /**
   * Wrap a position object so every set to `.x` or `.y` records a mutation
   * (when enabled). Returns the WRAPPED object — caller must replace any
   * reference. Pure no-op if already instrumented (idempotent).
   *
   * Implementation note: we use Object.defineProperty(getter/setter) rather
   * than a Proxy because PhysicsEntity.position is read on every physics
   * tick (60Hz) and Proxy traps add measurable overhead even for reads.
   * Defining accessors directly is ~free for reads, and the write path
   * only does work when `enabled` is true.
   */
  instrument(positionObj: { x: number; y: number }): { x: number; y: number } {
    if ((positionObj as any).__tracker_instrumented) return positionObj;
    let _x = positionObj.x;
    let _y = positionObj.y;
    const self = this;
    Object.defineProperty(positionObj, 'x', {
      get() { return _x; },
      set(v: number) {
        if (self.enabled && v !== _x) {
          self.record('x', _x, v);
        }
        _x = v;
      },
      configurable: true,
      enumerable: true,
    });
    Object.defineProperty(positionObj, 'y', {
      get() { return _y; },
      set(v: number) {
        if (self.enabled && v !== _y) {
          self.record('y', _y, v);
        }
        _y = v;
      },
      configurable: true,
      enumerable: true,
    });
    Object.defineProperty(positionObj, '__tracker_instrumented', {
      value: true,
      writable: false,
      enumerable: false,
      configurable: false,
    });
    this.instrumented = true;
    return positionObj;
  }

  private record(axis: 'x' | 'y', prev: number, next: number): void {
    const rawStack = new Error().stack ?? '';
    const at = Date.now();
    const mutation: PositionMutation = {
      at,
      axis,
      prevValue: prev,
      newValue: next,
      delta: next - prev,
      rawStack,
    };
    this.buffer.push(mutation);
    if (this.buffer.length > RING_CAP) {
      this.buffer.shift();
    }
    this.totalCount++;
    // Throttled structured-log emit for MCP grep.
    const source = labelSource(rawStack);
    const last = this.lastEmitBySource.get(source) ?? 0;
    if (at - last >= STRUCTURED_LOG_MIN_GAP_MS) {
      this.lastEmitBySource.set(source, at);
      gameLogger.warn('pos-mutate', `[${axis}] ${prev.toFixed(1)} → ${next.toFixed(1)} (Δ${(next - prev).toFixed(1)}) from ${source}`, {
        at, axis, prev, next, delta: next - prev, source,
      });
    }
  }

  getStats(): PositionMutationStats {
    const now = Date.now();
    const perSourceTotal: Map<string, number> = new Map();
    const perSourceWindow: Map<string, number> = new Map();
    let mutationsLast10s = 0;
    const recent: PositionMutationStats['recentMutations'] = [];

    for (const m of this.buffer) {
      const source = labelSource(m.rawStack);
      perSourceTotal.set(source, (perSourceTotal.get(source) ?? 0) + 1);
      if (now - m.at < WINDOW_MS) {
        perSourceWindow.set(source, (perSourceWindow.get(source) ?? 0) + 1);
        mutationsLast10s++;
      }
    }

    // Most-recent 20 mutations, labeled.
    for (const m of this.buffer.slice(-20)) {
      recent.push({
        at: m.at,
        axis: m.axis,
        prevValue: m.prevValue,
        newValue: m.newValue,
        delta: m.delta,
        source: labelSource(m.rawStack),
      });
    }

    const sortDesc = (a: { count: number }, b: { count: number }) => b.count - a.count;
    return {
      enabled: this.enabled,
      instrumented: this.instrumented,
      totalMutations: this.totalCount,
      mutationsLast10s,
      perSourceTotal: Array.from(perSourceTotal, ([source, count]) => ({ source, count })).sort(sortDesc),
      perSourceLast10s: Array.from(perSourceWindow, ([source, count]) => ({ source, count })).sort(sortDesc),
      recentMutations: recent,
    };
  }

  clear(): void {
    this.buffer = [];
    this.totalCount = 0;
    this.lastEmitBySource.clear();
  }

  /** Diagnostic — expose the raw buffer (with stacks) so external Playwright
   *  scripts can dig into the full stack chain when the labeled source is
   *  unhelpful in minified builds. Returns a defensive copy. */
  getRecentWithStacks(limit = 30): PositionMutation[] {
    return this.buffer.slice(-limit);
  }
}

/**
 * Strip frames that are deterministically known to be tracker-internal,
 * then label the first remaining frame.
 *
 * The stack structure on every `record()` invocation is:
 *   Line 0: "Error" header
 *   Line 1: `Lde.record (...)` — the Tracker's record method
 *   Line 2: `Object.set` or `set y` — the defineProperty accessor
 *   Line 3+: the actual caller we want to identify
 *
 * Skipping the first 2 frames after the header is bulletproof across
 * dev (source-mapped) AND production (minified) builds — it doesn't
 * depend on matching mangled class names.
 *
 * After the deterministic skip, we apply a smaller noise filter for
 * uninteresting downstream frames (react-internal, raf) so the surfaced
 * label is the first user-source frame.
 */
const NOISE_PATTERNS = [
  /react-dom\.development/,
  /scheduler\.development/,
  /node:internal/,
];

const SKIP_AFTER_HEADER = 2;  // record() + setter accessor

interface ParsedFrame {
  fn: string | null;
  file: string;
  lineNum: string;
}

function parseFrame(line: string): ParsedFrame | null {
  const withFn = line.match(/at\s+(.*?)\s+\((?:.*\/)?([^/?:#)]+\.[tj]sx?):(\d+):\d+\)/);
  if (withFn) {
    return { fn: withFn[1] || null, file: withFn[2], lineNum: withFn[3] };
  }
  const noFn = line.match(/at\s+(?:.*\/)?([^/?:#)]+\.[tj]sx?):(\d+):\d+/);
  if (noFn) {
    return { fn: null, file: noFn[1], lineNum: noFn[2] };
  }
  return null;
}

export function labelSource(rawStack: string): string {
  if (!rawStack) return '(no stack)';
  const allLines = rawStack.split('\n');
  // Strip "Error" header line, then skip 2 internal frames.
  const lines = allLines.slice(1 + SKIP_AFTER_HEADER).filter(l => l.trim());
  const interesting: ParsedFrame[] = [];
  for (const line of lines) {
    if (NOISE_PATTERNS.some(p => p.test(line))) continue;
    const parsed = parseFrame(line);
    if (parsed) interesting.push(parsed);
    if (interesting.length >= 3) break;  // Top-3 is plenty of context
  }
  if (interesting.length === 0) {
    return lines[0]?.trim().slice(0, 80) ?? '(unknown)';
  }
  const top = interesting[0];
  // When the immediate caller has a recognizable function name, use it
  // alone — it's the most actionable label.
  if (top.fn && top.fn !== 'anonymous' && top.fn !== 'Object.<anonymous>') {
    return `${top.file}:${top.lineNum} (${top.fn})`;
  }
  // Anonymous frame at line N — surface the next frame for context, so
  // minified bundles where the immediate caller is an arrow function
  // still produce an actionable label. Format: file:line ← parentFn.
  const parent = interesting[1];
  if (parent) {
    const parentLabel = parent.fn ? parent.fn : `${parent.file}:${parent.lineNum}`;
    return `${top.file}:${top.lineNum} ← ${parentLabel}`;
  }
  return `${top.file}:${top.lineNum}`;
}

export const PositionMutationTracker = new PositionMutationTrackerImpl();

// Expose to devtools for runtime debugging.
if (typeof window !== 'undefined') {
  (window as any).__POSITION_MUTATION_TRACKER__ = PositionMutationTracker;
}
